import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Link } from '@mui/material';
import { styled } from '@mui/material/styles';

const BannerWrapper = styled(Box)(({ show }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: '#1a1a1a',
  borderTop: '2px solid #FF6B35',
  padding: '20px',
  transform: show ? 'translateY(0)' : 'translateY(100%)',
  transition: 'transform 0.3s ease-in-out',
  zIndex: 9999,
  boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.5)',
}));

const BannerContent = styled(Box)({
  maxWidth: '1200px',
  margin: '0 auto',
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  alignItems: { xs: 'flex-start', md: 'center' },
  justifyContent: 'space-between',
  gap: '20px',
});

const TextSection = styled(Box)({
  flex: 1,
});

const BannerText = styled(Typography)({
  fontSize: '14px',
  color: '#D1D5DB',
  lineHeight: 1.6,
  marginBottom: '8px',
});

const PolicyLink = styled(Link)({
  color: '#FF6B35',
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
});

const ButtonSection = styled(Box)({
  display: 'flex',
  gap: '12px',
  flexDirection: { xs: 'column', sm: 'row' },
  width: { xs: '100%', md: 'auto' },
});

const AcceptButton = styled(Button)({
  backgroundColor: '#FF6B35',
  color: '#ffffff',
  padding: '10px 24px',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '8px',
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: '#E55A28',
  },
});

const DeclineButton = styled(Button)({
  backgroundColor: 'transparent',
  color: '#9CA3AF',
  border: '1px solid #4B5563',
  padding: '10px 24px',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '8px',
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: 'rgba(75, 85, 99, 0.1)',
    borderColor: '#6B7280',
  },
});

const CookieConsent = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Show banner after a short delay
      setTimeout(() => setShow(true), 1000);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookie_consent', 'accepted');
    setShow(false);
    // Initialize tracking if not already done
    // (Google Analytics and Meta Pixel are loaded on page load regardless)
  };

  const handleDecline = () => {
    localStorage.setItem('cookie_consent', 'declined');
    setShow(false);
    // Optionally disable tracking here
    // Note: This is a simplified version. Full GDPR compliance would require
    // not loading GA/Meta Pixel until user accepts
  };

  if (!show) return null;

  return (
    <BannerWrapper show={show}>
      <BannerContent>
        <TextSection>
          <BannerText>
            We use cookies and similar technologies (Google Analytics, Meta Pixel) to improve your experience, 
            analyze site traffic, and personalize content. By clicking "Accept", you consent to our use of cookies.
          </BannerText>
          <BannerText sx={{ fontSize: '13px', color: '#9CA3AF' }}>
            Learn more in our{' '}
            <PolicyLink href="/privacy">Privacy Policy</PolicyLink>
            {' '}and{' '}
            <PolicyLink href="/terms">Terms of Service</PolicyLink>.
          </BannerText>
        </TextSection>
        <ButtonSection>
          <DeclineButton onClick={handleDecline}>
            Decline
          </DeclineButton>
          <AcceptButton onClick={handleAccept}>
            Accept All
          </AcceptButton>
        </ButtonSection>
      </BannerContent>
    </BannerWrapper>
  );
};

export default CookieConsent;
